import React from 'react';
import { Lock, ShieldCheck } from 'lucide-react';

/**
 * Trust signals block that surfaces card brand acceptance + security badges
 * directly under the PayPal button. Increases credibility for new merchants
 * by communicating that customers can pay with any major card without
 * needing a PayPal account (PayPal's Guest Checkout flow).
 */
export const PaymentTrustBlock = () => {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm" data-testid="payment-trust-block">
      <p className="text-center text-xs uppercase tracking-wider font-bold text-gray-600 mb-3">
        All major cards accepted · Secure checkout
      </p>

      {/* Card brand badges */}
      <div className="flex items-center justify-center gap-1.5 flex-nowrap mb-4 overflow-x-auto">
        {/* Visa */}
        <div
          aria-label="Visa accepted"
          className="bg-white border border-gray-200 rounded-md px-2.5 py-1.5 shadow-sm flex items-center justify-center min-w-[50px] h-9 flex-shrink-0"
        >
          <span className="text-[#1A1F71] font-extrabold italic text-sm tracking-tight">VISA</span>
        </div>

        {/* Mastercard */}
        <div
          aria-label="Mastercard accepted"
          className="bg-white border border-gray-200 rounded-md px-2.5 py-1.5 shadow-sm flex items-center justify-center min-w-[50px] h-9 flex-shrink-0"
        >
          <div className="flex items-center">
            <div className="w-4 h-4 bg-[#EB001B] rounded-full" />
            <div className="w-4 h-4 bg-[#F79E1B] rounded-full -ml-2 mix-blend-multiply" />
          </div>
        </div>

        {/* American Express */}
        <div
          aria-label="American Express accepted"
          className="bg-[#016FD0] rounded-md px-2 py-1.5 shadow-sm flex items-center justify-center min-w-[50px] h-9 flex-shrink-0"
        >
          <span className="text-white font-bold text-[8px] leading-tight tracking-tight text-center">
            AMERICAN<br />EXPRESS
          </span>
        </div>

        {/* Discover */}
        <div
          aria-label="Discover accepted"
          className="bg-white border border-gray-200 rounded-md px-2 py-1.5 shadow-sm flex items-center justify-center min-w-[60px] h-9 flex-shrink-0"
        >
          <span className="text-[#FF6000] font-extrabold text-[10px] tracking-tight">DISCOVER</span>
        </div>

        {/* PayPal */}
        <div
          aria-label="PayPal accepted"
          className="bg-white border border-gray-200 rounded-md px-2.5 py-1.5 shadow-sm flex items-center justify-center min-w-[50px] h-9 flex-shrink-0"
        >
          <span className="font-extrabold italic text-xs tracking-tight">
            <span className="text-[#003087]">Pay</span><span className="text-[#009CDE]">Pal</span>
          </span>
        </div>
      </div>

      {/* Security badges */}
      <div className="flex items-center justify-center gap-4 flex-wrap pt-3 border-t border-gray-100">
        <div className="flex items-center gap-1.5 text-xs text-gray-700">
          <Lock className="w-3.5 h-3.5 text-green-600" />
          <span className="font-semibold">256-bit SSL Encrypted</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-700">
          <ShieldCheck className="w-3.5 h-3.5 text-green-600" />
          <span className="font-semibold">PCI-DSS Compliant Checkout</span>
        </div>
      </div>
    </div>
  );
};
